import React, { useEffect, useState } from 'react';

// Styles & Assets
import styles from './ViewPreconfigDialog.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import useTranslate from '../../../hooks/useTranslate';
import useHttp from '../../../hooks/useHttp';

// Components
import EnvironmentsString from '../../Helpers/EnvironmentsString';

// Interfaces
import { Preconfiguration } from '../../../interfaces/Preconfigurations';

// Language
import { useTranslation } from 'react-i18next';
import { allNullFrequencyValues, getFrequencyPoll, getSpecificFrequency } from '../../../services/getPollAttribute';
import { getPollFrequency } from '../../../services/Poll';
import { Parameter } from '../../../interfaces/Profile';

interface Props {
    preconfiguration: Preconfiguration
    showModal: React.Dispatch<React.SetStateAction<boolean>>
}

const ViewPreconfigDialog: React.FC<Props> = ({ preconfiguration, showModal }) => {
    const { t } = useTranslation();
    const { translateMiddleware } = useTranslate();

    const { sendRequest } = useHttp<Preconfiguration>();
    const [preconfigDetail, setPreconfigDetail] = useState<Preconfiguration>(preconfiguration);

    const headerCells = [
        t('vital_signs'),
        t('minimum_alert_value'),
        t('maximum_alert_value'),
        t('frequency'),
        t('reminder')
    ];

    const headerCellsPolls = [
        t('polls'),
        '',
        '',
        t('frequency'),
        t('reminder')
    ];

    useEffect(() => {
        if (!preconfiguration?.id) return;

        sendRequest(
            {
                url: getEnvironmentApiUrl() + 'preconfig/' + preconfiguration.id,
                method: 'GET',
            },
            (data) => {
                setPreconfigDetail(data);
            }
        );
    }, [preconfiguration?.id, sendRequest]);

    const profiles = preconfigDetail?.profiles ?? [];
    const pathologies = preconfigDetail?.pathologies ?? [];
    const sheetParams = preconfigDetail?.preconfig_sheet_parameters ?? [];
    const inquiries = preconfigDetail?.preconfig_inquiries ?? [];

    const getParameterName = (parameter: Parameter | undefined, sheetParameterId?: string) =>
        translateMiddleware(parameter?.name_translations)?.text ??
        parameter?.name ??
        sheetParameterId ??
        parameter?.id ??
        '';

    return (
        <div className={`${styles.dialog} dialog`}>
            <h1 className='dialogTitle'>{t('preconfiguration')}</h1>
            <h2 className={`${styles.subtitle} dialogSubTitle`}>{preconfigDetail?.name ?? ''}</h2>

            <div className={styles.content}>
                <h3 className={styles.titleSection}>{t('environment')}</h3>
                {profiles.length ? (
                    <p>
                        <EnvironmentsString profileIDs={profiles.map(profile => profile.id)} />
                    </p>
                ) : (
                    <p>-</p>
                )}

                <h3 className={styles.titleSection}>{t('tags')}</h3>
                <div className={styles.pathologiesContainer}>
                    {pathologies.length ? (
                        pathologies.map(p => (
                            <div key={p.id}>{p.name ?? ''}</div>
                        ))
                    ) : (
                        <div>-</div>
                    )}
                </div>

                <h3 className={styles.titleSection}>{t('vital_signs')}</h3>
                <table className={styles.table}>
                    <thead>
                        <tr className={styles.headerRow}>
                            {headerCells.map(cell => <th key={cell}>{cell}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {sheetParams.length ? (
                            sheetParams.map(sheetParameter => (
                                <tr key={sheetParameter.id ?? sheetParameter.sheet_parameter_id}>
                                    <td className={styles.cell}>
                                        {getParameterName(
                                            sheetParameter.sheet_parameter,
                                            sheetParameter.sheet_parameter_id
                                        )}
                                    </td>
                                    <td className={styles.cell}>{sheetParameter.min_limit ?? '-'}</td>
                                    <td className={styles.cell}>{sheetParameter.max_limit ?? '-'}</td>
                                    <td className={styles.cell}>
                                        {allNullFrequencyValues(sheetParameter)
                                            ? '-'
                                            : t(getFrequencyPoll(sheetParameter))}
                                    </td>
                                    <td className={styles.cell}>{sheetParameter.reminder_time ?? '-'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className={styles.cell} colSpan={5}>-</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <h3 className={styles.titleSection}>{t('polls')}</h3>
                <table className={styles.table}>
                    <thead>
                        <tr className={styles.headerRow}>
                            {headerCellsPolls.map(cell => <th key={cell}>{cell}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {inquiries.length ? (
                            inquiries.map(pInquiry => (
                                <tr key={pInquiry.id ?? pInquiry.inquiry_id}>
                                    <td className={styles.cell}>
                                        {pInquiry.inquiry?.name ?? pInquiry.inquiry_id}
                                    </td>
                                    <td className={styles.cell}></td>
                                    <td className={styles.cell}></td>
                                    <td className={styles.cell}>
                                        {t(getSpecificFrequency(getPollFrequency(pInquiry), pInquiry.inquiry ?? null))}
                                    </td>
                                    <td className={styles.cell}>
                                        {pInquiry.reminder_time ?? '-'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className={styles.cell} colSpan={5}>-</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className={styles.buttonCloseContainer}>
                <button onClick={() => showModal(false)} className='button'>
                    {t('close')}
                </button>
            </div>
        </div>
    );
};

export default ViewPreconfigDialog;